/**
 * ScenariosPage — Interactive slider-driven scenario analysis.
 * Debounced parameter changes hit the server-side compute endpoint.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui';
import { useForecastSummary, useComputeScenario } from './api';
import { formatCurrency, formatWeekLabel, liquidityBg } from './utils';
import { cn } from '../../shared/lib/cn';
import type { ScenarioParams, WeeklyForecastRow } from './types';
import { DEFAULT_SCENARIO_PARAMS } from './types';

const DEBOUNCE_MS = 200;

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (v: number) => void;
}

function Slider({ label, value, min, max, step, unit, onChange }: SliderProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{label}</label>
        <span className="text-xs font-mono tabular-nums text-charcoal dark:text-gray-200">
          {value}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 bg-gray-200 dark:bg-dark-border rounded-lg appearance-none cursor-pointer accent-eliza-red"
      />
      <div className="flex justify-between text-[10px] text-gray-400">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
}

export default function ScenariosPage() {
  const { data: summary } = useForecastSummary();
  const computeScenario = useComputeScenario();

  const [params, setParams] = useState<ScenarioParams>(DEFAULT_SCENARIO_PARAMS);
  const [scenarioWeeks, setScenarioWeeks] = useState<WeeklyForecastRow[] | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const forecastId = summary?.forecast_id;

  const debouncedCompute = useCallback(
    (newParams: ScenarioParams) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (!forecastId) return;
        computeScenario.mutate(
          { forecast_id: forecastId, ...newParams },
          { onSuccess: (data) => setScenarioWeeks(data.weeks) },
        );
      }, DEBOUNCE_MS);
    },
    [forecastId, computeScenario],
  );

  const updateParam = useCallback(
    <K extends keyof ScenarioParams>(key: K, value: ScenarioParams[K]) => {
      setParams(prev => {
        const next = { ...prev, [key]: value };
        debouncedCompute(next);
        return next;
      });
    },
    [debouncedCompute],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const displayWeeks = scenarioWeeks ?? summary?.weeks ?? [];
  const baseWeeks = summary?.weeks ?? [];
  const isModified = JSON.stringify(params) !== JSON.stringify(DEFAULT_SCENARIO_PARAMS);

  return (
    <div className="space-y-6 mt-2">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Parameter Sliders */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardContent className="p-4 space-y-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">Parameters</h3>
                {isModified && (
                  <button
                    onClick={() => { setParams(DEFAULT_SCENARIO_PARAMS); setScenarioWeeks(null); }}
                    className="text-[10px] text-eliza-red hover:underline"
                  >
                    Reset
                  </button>
                )}
              </div>

              <Slider
                label="AR Collection Delay"
                value={params.ar_collection_delay_days}
                min={-14}
                max={21}
                step={1}
                unit=" days"
                onChange={v => updateParam('ar_collection_delay_days', v)}
              />
              <Slider
                label="AR Collection Rate"
                value={params.ar_collection_rate}
                min={50}
                max={100}
                step={1}
                unit="%"
                onChange={v => updateParam('ar_collection_rate', v)}
              />
              <Slider
                label="AP Payment Acceleration"
                value={params.ap_payment_accel_days}
                min={-14}
                max={14}
                step={1}
                unit=" days"
                onChange={v => updateParam('ap_payment_accel_days', v)}
              />
              <Slider
                label="Revenue Pipeline"
                value={params.revenue_pipeline_multiplier}
                min={0}
                max={150}
                step={5}
                unit="%"
                onChange={v => updateParam('revenue_pipeline_multiplier', v)}
              />
              <Slider
                label="Payroll Adjustment"
                value={params.payroll_adjustment_pct}
                min={90}
                max={110}
                step={1}
                unit="%"
                onChange={v => updateParam('payroll_adjustment_pct', v)}
              />

              <div className="pt-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">CapEx Timing</label>
                <select
                  value={params.capex_timing}
                  onChange={e => updateParam('capex_timing', e.target.value as 'as_planned' | 'defer_all')}
                  className="mt-1 w-full text-sm border border-gray-200 dark:border-dark-border rounded-lg px-3 py-1.5 bg-white dark:bg-dark-surface"
                >
                  <option value="as_planned">As Planned</option>
                  <option value="defer_all">Defer All</option>
                </select>
              </div>

              {computeScenario.isPending && (
                <p className="text-[10px] text-gray-400 animate-pulse">Computing...</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Results Table */}
        <div className="lg:col-span-3">
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
                <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">
                  {isModified ? 'Scenario Result' : 'Base Forecast'}
                </h3>
                {isModified && (
                  <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Modified</span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-dark-border">
                      <th className="text-left py-2 px-3 font-medium text-gray-500 sticky left-0 bg-white dark:bg-dark-surface">Week</th>
                      <th className="text-right py-2 px-2 font-medium text-gray-500">Receipts</th>
                      <th className="text-right py-2 px-2 font-medium text-gray-500">Disbursements</th>
                      <th className="text-right py-2 px-2 font-medium text-gray-500">Net</th>
                      <th className="text-right py-2 px-2 font-medium text-gray-500">Ending Cash</th>
                      <th className="text-center py-2 px-2 font-medium text-gray-500">Status</th>
                      {isModified && <th className="text-right py-2 px-2 font-medium text-gray-500">Δ vs Base</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-dark-border/50">
                    {displayWeeks.map((w, i) => {
                      const base = baseWeeks[i];
                      const delta = base ? w.ending_cash - base.ending_cash : 0;
                      return (
                        <tr key={w.week_number} className="hover:bg-gray-50 dark:hover:bg-dark-surface-2/50">
                          <td className="py-1.5 px-3 sticky left-0 bg-inherit font-medium text-charcoal dark:text-gray-200">
                            W{w.week_number}
                            <span className="ml-1 text-[10px] text-gray-400">{formatWeekLabel(w.week_start_date)}</span>
                          </td>
                          <td className="py-1.5 px-2 text-right tabular-nums text-emerald-600">{formatCurrency(w.total_receipts, true)}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums text-red-600">{formatCurrency(w.total_disbursements, true)}</td>
                          <td className={cn(
                            'py-1.5 px-2 text-right tabular-nums font-medium',
                            w.net_cash_flow >= 0 ? 'text-emerald-600' : 'text-red-600',
                          )}>
                            {formatCurrency(w.net_cash_flow, true)}
                          </td>
                          <td className="py-1.5 px-2 text-right tabular-nums font-semibold text-charcoal dark:text-gray-200">
                            {formatCurrency(w.ending_cash, true)}
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <span className={cn('inline-block px-1.5 py-0.5 rounded text-[10px] font-medium', liquidityBg(w.liquidity_status))}>
                              {w.liquidity_status}
                            </span>
                          </td>
                          {isModified && (
                            <td className={cn(
                              'py-1.5 px-2 text-right tabular-nums text-xs',
                              delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-600' : 'text-gray-400',
                            )}>
                              {delta > 0 ? '+' : ''}{formatCurrency(delta, true)}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
